import {SequenceAnnotationConfig} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import startDomainSequenceAnnotation from 'antibodies/domain-annotation/startDomainSequenceAnnotation';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';

/**
 * Returns the IDs of the entities registered from rows that were unregistered when the user submitted, each
 * once and in row order.
 *
 * The registration response also includes the entities of submitted rows that were already registered, since
 * registration updates them too. Those are skipped so that only new registrations are annotated. Batched
 * registration concatenates its batches' responses in row order, so batching needs no special handling.
 *
 * @param rows The table rows as they were when the user submitted.
 * @param firstSubmittedRowIndex The index in `rows` of the first submitted row.
 * @param registeredEntityIds The entity IDs from a successful registration response: one per submitted row, in
 * row order, starting at `firstSubmittedRowIndex`.
 * @param unregisteredRowIds The rows that were unregistered when the user submitted.
 */
export function getNewlyRegisteredSequenceIds({
  rows,
  firstSubmittedRowIndex,
  registeredEntityIds,
  unregisteredRowIds,
}: {
  rows: Array<Pick<StructuredTableRow, 'rowId'>>;
  firstSubmittedRowIndex: number;
  registeredEntityIds: Array<string>;
  unregisteredRowIds: Array<string>;
}): Array<string> {
  const unregisteredRowIdSet = new Set(unregisteredRowIds);
  const sequenceIds = registeredEntityIds.filter((_entityId, index) => {
    const row = rows[firstSubmittedRowIndex + index];
    return row != null && unregisteredRowIdSet.has(row.rowId);
  });
  // Merging during registration can register several rows as the same entity, and annotation rejects
  // requests that list a sequence more than once.
  return Array.from(new Set(sequenceIds));
}

/**
 * Starts annotating the sequences newly registered by a registration table submit, as selected by
 * `getNewlyRegisteredSequenceIds`. Annotation failures are reported on its tray tasks, so they do not affect
 * registration.
 */
export default function annotateRegisteredSequences({
  config,
  ...params
}: Parameters<typeof getNewlyRegisteredSequenceIds>[0] & {config: SequenceAnnotationConfig}): void {
  startDomainSequenceAnnotation({
    sequenceIds: getNewlyRegisteredSequenceIds(params),
    config,
    actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_REGISTRATION_TABLE,
  });
}
